import { Component, OnInit, OnDestroy, ViewChild, Input, inject } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { AuthService } from '../services/auth.service';
import { EhrComponent } from './ehr/ehr.component';
import { AdcComponent } from './adc/adc.component';
import { NormalizedDataComponent } from './normalized-data/normalized-data.component';
import { UrcComponent } from './urc/urc.component';
import { ActivatedRoute } from "@angular/router";
import { environment } from "src/environments/environment";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { cloneFilterState } from '../services/data-filter.service';
import {
  addUnreconciledDoseResolutions,
  hasUnreconciledDoseResolution,
  hasPartialUnreconciledDoseResolution,
  removeUnreconciledDoseResolutions
} from './unreconciled-dose-filter.util';

@Component({
    selector: 'app-variance',
    templateUrl: './variance.component.html',
    styleUrls: ['./variance.component.css'],
    standalone: false
})

export class VarianceComponent implements OnInit, OnDestroy {
  authService = inject(AuthService);
  private route = inject(ActivatedRoute);

  moduleName="Variance";
  selectedDays = 7;
  days: SelectItem[] = [
    {label: '7 Days', value: 7},
    {label: '30 Days', value: 30},
    {label: '90 Days', value: 90}
  ];
  include_csn = environment.include_csn
  showMultiAdminsCheckbox = true
  showUnreconciledDosesOnly = false;
  activeTab = '3';
  @ViewChild('adc') adc!: AdcComponent;
  @ViewChild('ehr') ehr!: EhrComponent;
  @ViewChild('normData') normData!: NormalizedDataComponent;
  @ViewChild('urc') urc!: UrcComponent;
  
  @Input() deId=false;
  dissableDeIdSwitch = false;
  filterValues:any = {};
  dateRange: { startDate: Date; endDate: Date } | null = null;
  private readonly destroy$ = new Subject<void>();


  ngOnInit(): void {
    const deIdState = this.authService.getDeIdState();
    this.deId = deIdState.value;
    this.dissableDeIdSwitch = deIdState.disabled;
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe(x => {
      this.deId = this.authService.isDemoUser() ? true : x.deId;
    });
    const body = document.getElementsByTagName('body')[0];
    body.className = "";
    body.classList.add('subpage');
    body.classList.add('variance');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateLookbackPeriod(lookbackPeriod: number): void {
    this.selectedDays = lookbackPeriod;
    this.adc?.updateLookbackPeriod(lookbackPeriod);
    this.ehr?.updateLookbackPeriod(lookbackPeriod);
    this.normData?.updateLookbackPeriod(lookbackPeriod);
    this.urc?.updateLookbackPeriod(lookbackPeriod);
  }

  tabChanged(event: any){
    this.activeTab = String(event);
    this.filterValues = {};
    this.dateRange = null;
    this.showUnreconciledDosesOnly = false;

    if(event == '0'){
      this.moduleName = "ADC";
      this.showMultiAdminsCheckbox = true;
      this.adc?.refreshToInitialState();
    }
    if(event == '1'){
      this.moduleName = "EHR";
      this.showMultiAdminsCheckbox = true;
      this.ehr?.refreshToInitialState();
    }
    if(event == '2'){
      this.moduleName = "NORMALIZED DATA";
      this.showMultiAdminsCheckbox = true;
      this.normData?.refreshToInitialState();
    }
    if(event == '3'){
      this.moduleName = "DISCREPANCIES";
      this.showMultiAdminsCheckbox = true;
      this.urc?.refreshToInitialState();
    }
  }

  updatedFilterValue(filter: any){
    if (this.isVarianceModule() && this.showUnreconciledDosesOnly) {
      const filterValues = { ...(filter || {}) };
      if (hasPartialUnreconciledDoseResolution(filterValues)) {
        this.showUnreconciledDosesOnly = false;
        this.urc.updateUnreconciledOnly(false, false, false);
      } else {
        addUnreconciledDoseResolutions(filterValues);
      }
      this.filterValues = filterValues;
    } else {
      this.filterValues = cloneFilterState(filter || {});
    }
  }
  
  removeFilter(filter: any){
    if (this.isVarianceModule() && this.showUnreconciledDosesOnly && !hasUnreconciledDoseResolution(filter)) {
      this.showUnreconciledDosesOnly = false;
      this.urc.updateUnreconciledOnly(false, false);
    }

    if(this.isVarianceModule()){
      this.urc.removeVarianceFilters(filter, this.selectedDays);
    } else if(this.moduleName == "ADC"){
      this.adc.removeVarianceFilters(filter);
    } else if(this.moduleName == "EHR"){
      this.ehr.removeVarianceFilters(filter);
    } else if(this.moduleName.toUpperCase() == "NORMALIZED DATA"){
      this.normData.removeVarianceFilters(filter);
    }
  }

  openFilterModuleAfterMore(filterInputs: any){
    if(this.isVarianceModule()){
      this.urc.openVarianceFilter(filterInputs);
    } else if(this.moduleName == "ADC"){
      this.adc.openVarianceFilter(filterInputs);
    } else if(this.moduleName == "EHR"){
      this.ehr.openVarianceFilter(filterInputs);
    } else if(this.moduleName.toUpperCase() == "NORMALIZED DATA"){
      this.normData.openVarianceFilter(filterInputs);
    }
  }

  updatedDate(value: { startDate?: string | Date; endDate?: string | Date } | null): void {
    const startDate = this.toLocalBoundary(value?.startDate, false);
    const endDate = this.toLocalBoundary(value?.endDate, true);

    this.dateRange = startDate && endDate ? { startDate, endDate } : null;
  }

  changeShowUnreconciledDosesOnly(){
    this.showUnreconciledDosesOnly = this.showUnreconciledDosesOnly ? true : false;
    this.syncUnreconciledDoseFilterValues(this.showUnreconciledDosesOnly);
    this.urc.updateUnreconciledOnly(this.showUnreconciledDosesOnly);
  }

  private isVarianceModule(): boolean {
    return this.moduleName == "Variance" || this.moduleName == "DISCREPANCIES";
  }

  private syncUnreconciledDoseFilterValues(showUnreconciledDosesOnly: boolean): void {
    const filterValues = { ...this.filterValues };
    if (showUnreconciledDosesOnly) {
      addUnreconciledDoseResolutions(filterValues);
    } else {
      removeUnreconciledDoseResolutions(filterValues);
    }
    this.filterValues = filterValues;
  }

  linkRecords(){
    this.urc.linkSelectedRecords()
  }

  updateDeIdMode(value: boolean) {
    if (!this.authService.isDemoUser()) {
      this.deId = value;
      this.authService.setDeIdentified(value);
    }
  }

  private toLocalBoundary(value: string | Date | undefined, endOfDay: boolean): Date | null {
    if (!value) {
      return null;
    }

    const dateParts = value instanceof Date
      ? [value.getFullYear(), value.getMonth() + 1, value.getDate()]
      : String(value).slice(0, 10).split('-').map(Number);
    if (dateParts.length !== 3 || dateParts.some(part => !Number.isFinite(part))) {
      return null;
    }

    const date = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
    if (
      Number.isNaN(date.getTime()) ||
      date.getFullYear() !== dateParts[0] ||
      date.getMonth() !== dateParts[1] - 1 ||
      date.getDate() !== dateParts[2]
    ) {
      return null;
    }

    date.setHours(endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
    return date;
  }
}
